import { effect, inject, Injectable, Signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    AuthorizationsObject,
    AuthService,
    OAuthProfile,
    UserProfileService,
} from '@dat/angular-auth';
import {
    IqEventBusBrokerService,
    IqEventBusQueueService,
} from '@dat/iq-event-bus';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { isEmpty } from 'lodash';
import { filter, map, Observable, switchMap, take } from 'rxjs';
import { IqTrackingSegmentMapperService } from './iq-tracking-segment-mapper.service';
import {
    ApplicationInfo,
    ApplicationPayload,
    ApplicationPayloadSchema,
    EventRow,
    IdentifyTracking,
    RegisterPayload,
    RegisterPayloadSchema,
    TrackingEvent,
    TrackingEventTypeSchema,
} from './iq-tracking-segment.models';
import { SegmentLibraryLoader } from './segment-library-loader.service';
import { IqDatadogLogsService } from '@dat/datiq-observability';

/**
 * Making this any due to the fact that because of how segment loads in the browser
 * trying to use the exact typing for this breaks the tests.
 * TODO: Explore how to instantiate Segment using an inject token and potentially use
 *       it for tests going forward.
 */
declare var analytics: any;

type IqTrackingSegmentState = {
    identified: boolean;
    userLoaded: boolean;
    page: string;
    appInfo: ApplicationInfo;
    events: Record<string, EventRow>;
};

@Injectable({
    providedIn: 'root',
})
export class IqTrackingSegmentService extends ComponentStore<IqTrackingSegmentState> {
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _auth: AuthService = inject(AuthService);
    private _loadLibrary: SegmentLibraryLoader = inject(SegmentLibraryLoader);
    private _user: UserProfileService = inject(UserProfileService);
    private _mapper: IqTrackingSegmentMapperService = inject(
        IqTrackingSegmentMapperService,
    );
    private _queue: IqEventBusQueueService = new IqEventBusQueueService();

    private readonly userProfile: Signal<OAuthProfile> = toSignal(
        this._user.userProfile$,
    );
    private readonly authorizations: Signal<AuthorizationsObject> = toSignal(
        this._auth.userAuthorizationsResult$,
    );
    private readonly _datadogLogs = inject(IqDatadogLogsService);

    constructor() {
        super({
            identified: false,
            page: '',
            userLoaded: false,
            appInfo: {},
            events: {},
        });

        effect(() => {
            if (!this.userLoaded()) return;

            const unhandled = this._queue.unhandledEventNodeMap();

            if (this._queue.isAllHandled()) return;

            const trackEvents = unhandled.get('TRACK');

            untracked(() => {
                trackEvents?.forEach((event) => {
                    const props = event.evt.payload as TrackingEvent;

                    this.track(props);
                    this._queue.handleEvent(event);
                });
            });
        });
    }

    //#region Effects

    /**
     * Initializes the segment tracking. This will call the identify method
     * once the user is logged in.
     */
    initialize = this.effect(($trigger) => {
        return $trigger.pipe(
            tapResponse({
                next: () => {
                    this._loadLibrary.injectSegmentLibrary();
                    this._registerEvents();
                },
                error: (error) => {
                    console.error(
                        'IqTrackingSegmentService initialization failed',
                        error,
                    );
                },
            }),
        );
    });

    track = this.effect(($trackEvent: Observable<TrackingEvent>) => {
        return $trackEvent.pipe(
            tapResponse({
                next: async (trackingEvent: TrackingEvent) => {
                    const rowEvent = this.rowEvent(trackingEvent.eventName)();

                    if (isEmpty(rowEvent)) return;

                    const props: Record<string, any> = this._mapper.track(
                        trackingEvent,
                        rowEvent,
                        this.state().appInfo,
                    );

                    this.sendTrackerToSegment(trackingEvent.eventName, {
                        ...props,
                        page: this.state().page,
                    });
                },
                error: (error) => {
                    console.error('Identify user failed', error);
                },
            }),
        );
    });

    identify = this.effect(($appInfo: Observable<ApplicationPayload>) => {
        return $appInfo.pipe(
            filter(() => !this.state().identified),
            switchMap((payload: ApplicationPayload) =>
                this._auth.loggedIn$.pipe(
                    map((loggedIn) => ({
                        payload,
                        loggedIn,
                    })),
                    take(1),
                ),
            ),
            filter(({ loggedIn }: { loggedIn: boolean }) => loggedIn),
            tapResponse({
                next: async ({
                    payload,
                    loggedIn,
                }: {
                    payload: ApplicationPayload;
                    loggedIn: boolean;
                }) => {
                    await this.sendIdentifyToSegment(payload);

                    this.patchState({
                        identified: true,
                    });
                },
                error: (error) => {
                    console.error('Identify user failed', error);
                },
            }),
        );
    });

    addUser = this.effect(($trigger) => {
        return $trigger.pipe(
            switchMap(() => this._user.userProfile$),

            filter((user: OAuthProfile) => !isNaN(user.csbId)),
            tapResponse({
                next: async (userProfile: OAuthProfile) => {
                    if (this.userLoaded()) return;

                    const userInfo: Partial<ApplicationInfo> =
                        this._mapper.userInfo(userProfile);

                    this.updateUserInfo(userInfo);
                },
                error: (error) => {
                    console.error('Identify user failed', error);
                },
            }),
        );
    });
    //#endregion

    //# region Selectors

    readonly userLoaded = this.selectSignal((state) => state.userLoaded);
    readonly rowEvent: (eventName: string) => Signal<EventRow> = (
        eventName: string,
    ) =>
        this.selectSignal<EventRow>(
            (state): EventRow => state.events[eventName],
        );

    //#endregion

    //#region Updaters

    updatePage = this.updater((state, page: string) => ({
        ...state,
        page,
    }));

    updateUserInfo = this.updater(
        (state, userInfo: Partial<ApplicationInfo>) => ({
            ...state,
            userLoaded: true,
            appInfo: {
                ...state.appInfo,
                ...userInfo,
            },
        }),
    );

    registerEvents = this.updater(
        (
            state,
            {
                events,
                appInfo,
            }: { events: Record<string, EventRow>; appInfo: ApplicationInfo },
        ) => ({
            ...state,
            events,
            appInfo,
        }),
    );

    //# endregion

    //#region Private methods

    async sendIdentifyToSegment(payload: ApplicationPayload): Promise<boolean> {
        const userProfile: OAuthProfile = this.userProfile();
        const authObj: AuthorizationsObject = this.authorizations();
        const userInfo: IdentifyTracking = this._mapper.identify(
            payload,
            userProfile,
            authObj,
        );
        this._datadogLogs.info('indentifying user to segment', { custom: { userInfo } });
        return new Promise<boolean>((resolve) =>
            analytics.identify(userInfo.user_id, { ...userInfo }, null, () =>
                resolve(true),
            ),
        );
    }

    async sendTrackerToSegment(
        eventName: string,
        props: Record<string, string>,
    ) {
        this._datadogLogs.info('sending track call to segment', { custom: { eventName, props } });
        return new Promise<boolean>((resolve) =>
            analytics.track(eventName, props, null, (...args) => {
                resolve(true);
            }),
        );
    }

    private _registerEvents(): void {
        /**
         * Listen for the identify the user and sends it to
         * segment tracking.
         */
        this._bus.on('type:segment:identify', (payload: ApplicationPayload) => {
            try {
                const parsed: ApplicationPayload =
                    ApplicationPayloadSchema.parse({
                        ...payload,
                        tracking_version: payload.tracking_version + '',
                    });

                this.identify(parsed);
            } catch (err) {
                console.error('Failed to identify user', err.message);
            }
        });
        this._bus.on(
            'type:route:changed',
            (payload: { analyticsPageName: string }) => {
                this.updatePage(payload.analyticsPageName);
            },
        );
        this._bus.on('type:tracking:register', (payload: RegisterPayload) => {
            if (payload.type !== 'SEGMENT') return;

            try {
                const parsedData = RegisterPayloadSchema.parse({
                    ...payload,
                    appInfo: {
                        ...payload.appInfo,
                        tracking_version: payload.appInfo.tracking_version + '',
                    },
                });

                this.registerEvents({
                    events: parsedData.events.reduce((acc, event) => {
                        return {
                            ...acc,
                            [event.row['Event Name']]: event.row,
                        };
                    }, {}),
                    appInfo: parsedData.appInfo,
                });
                this.addUser();
            } catch (err) {
                console.error('Failed to register events', err.message);
            }
        });

        this._bus.on('type:tracking:log', (payload: any) => {
            if (payload.type !== 'SEGMENT') return;
            try {
                const name = TrackingEventTypeSchema.parse(payload.eventType);

                this._queue.addEvent({
                    name,
                    payload: payload,
                });
            } catch (err) {
                console.error('Failed to log event', err.message);
            }
        });
    }
    //#endregion
}
